import { useEffect, useState } from 'react';
import './App.css';
import { SurveyCreatorRenderComponent } from './components/SurveyCreator/SurveyCreator';
import { sfpApplicationFormAPI } from './services/apiService';
import {
  applyUrlSearchParams,
  getDashboardLoginUrl,
  hasAuthSession,
  requestParentAuth,
  stripAuthParamsFromUrl,
} from './services/authSession';
import ToastContainer from './components/Toast/ToastContainer';
import useToast from './hooks/useToast';

function App() {
  const [customAssessment, setCustomAssessment] = useState({});
  const [formId, setFormId] = useState(null);
  const [sfpId, setSfpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const { toasts, removeToast, showSuccess, showError } = useToast();


  useEffect(() => {
    const initializeForm = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlFormId = params.get('id');
        const urlSfpId = params.get('sfp_id');
        
        console.log("Form ID from URL:", urlFormId);
        console.log("SFP ID from URL:", urlSfpId);

        applyUrlSearchParams(window.location.search);
        stripAuthParamsFromUrl();

        if (!hasAuthSession()) {
          await requestParentAuth();
        }

        if (urlFormId) {
          // Load existing form
          setFormId(urlFormId);
          await loadFormData(urlFormId);
        } else if (urlSfpId) {
          // Get forms for this SFP or create new one
          setSfpId(urlSfpId);
          await loadSfpForms(urlSfpId);
        } else {
          // No ID provided, start with empty form
          setCustomAssessment({});
          setLoading(false);
        }
      } catch (err) {
        // console.error('Error initializing form:', err);
        setError('Failed to initialize form');
        setLoading(false);
      }
    };

    initializeForm();
  }, []);

  const loadFormData = async (formId) => {
    try {
      setLoading(true);
      const response = await sfpApplicationFormAPI.getFormById(formId);
      
      if (response.status) {
        const form = response.data;
        setFormData(form);
        
        // Set survey JSON data
        const surveyJson = form.form_schema || form.survey_json || {};
        setCustomAssessment(surveyJson);
      } else {
        setError(response.msg || 'Failed to load form');
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const loadSfpForms = async (sfpId) => {
    try {
      setLoading(true);
      const response = await sfpApplicationFormAPI.getFormsBySFP(sfpId);
      
      console.log('SFP Forms API Response:', response);
      
      if (response.status && response.data && response.data.forms && response.data.forms.length > 0) {
        // Use the first form found
        const form = response.data.forms[0];
        setFormId(form._id);
        setFormData(form);
        
        const surveyJson = form.form_schema || form.survey_json || {};
        setCustomAssessment(surveyJson);
      } else {
        // No forms found for this SFP, start with empty form
        setCustomAssessment({});
      }
    } catch (err) {
      console.error('Error loading SFP forms:', err);
      // If error loading, start with empty form
      setCustomAssessment({});
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForm = async (surveyJson) => {
    try {
      const formPayload = {
        form_name: formData?.form_name || `Application Form ${Date.now()}`,
        form_type: 'SFP_APPLICATION',
        form_schema: surveyJson,
        survey_json: surveyJson,
        status: 'DRAFT',
        sfp_ref: sfpId,
        description: 'Application form created via Survey Creator'
      };

      let response;
      if (formId) {
        // Update existing form
        response = await sfpApplicationFormAPI.updateForm(formId, formPayload);
      } else {
        // Create new form
        response = await sfpApplicationFormAPI.createForm(formPayload);
        if (response.status && response.data) {
          setFormId(response.data._id);
          setFormData(response.data);
        }
      }

      if (response.status) {
        console.log('Form saved successfully:', response.data);
        showSuccess('Form saved successfully!');
        // Update URL with form ID if it was just created
        if (!formId && response.data?._id) {
          const newUrl = new URL(window.location);
          newUrl.searchParams.set('id', response.data._id);
          window.history.replaceState({}, '', newUrl);
        }
        return { success: true, data: response.data };
      } else {
        console.error('Failed to save form:', response.msg);
        showError(response.msg || 'Failed to save form');
        return { success: false, error: response.msg };
      }
    } catch (err) {
      console.error('Error saving form:', err);
      showError(err.message || 'An error occurred while saving');
      return { success: false, error: err.message };
    }
  };

  const handlePublishForm = async () => {
    try {
      if (!formId) {
        console.warn('Please save the form first before publishing');
        return;
      }

      const response = await sfpApplicationFormAPI.publishForm(formId);
      if (response.status) {
        console.log('Form published successfully!');
        showSuccess('Form published successfully!');
        // Update form data state with the published form
        setFormData(prevData => ({ ...prevData, ...response.data }));
        // Also refresh form data from server
        await loadFormData(formId);
      } else {
        console.error('Failed to publish form:', response.msg);
        showError(response.msg || 'Failed to publish form');
      }
    } catch (err) {
      console.error('Error publishing form:', err);
      showError(err.message || 'An error occurred while publishing');
    }
  };

  const checkAuthentication = () => hasAuthSession();

  const handleLoginWithTese = async () => {
    if (window.parent !== window) {
      const ok = await requestParentAuth();
      if (ok) {
        window.location.reload();
        return;
      }
    }

    const dashboardUrl = getDashboardLoginUrl(window.location.href);
    if (dashboardUrl) {
      window.location.href = dashboardUrl;
      return;
    }

    window.location.reload();
  };

  if (loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <div>Loading form...</div>
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            {formId ? 'Loading existing form...' : 'Initializing form creator...'}
          </div>
          {!checkAuthentication() && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#ff6b6b' }}>
              ⚠️ Authentication required - please log in to the main dashboard first
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          color: '#e74c3c'
        }}>
          <div>Error: {error}</div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show authentication warning if not authenticated
  if (!checkAuthentication()) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ 
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '20px', color: '#ff6b6b' }}>
              🔒 Authentication Required
            </div>
            <div style={{ fontSize: '16px', marginBottom: '20px', color: '#666' }}>
              You need to be logged in to TESE to access the survey creator.
            </div>
            <div style={{ fontSize: '14px', color: '#999' }}>
              If you opened this from the dashboard, retry to sync your SSO session.
              Otherwise sign in with TESE and come back.
            </div>
            <button 
              onClick={handleLoginWithTese}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Login with TESE
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div style={{ 
        // padding: '10px', 
        backgroundColor: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          {/* <h3 style={{ margin: 0 }}>SFP Application Form Creator</h3> */}
          {/* <div style={{ fontSize: '12px', color: '#666'}}>
            {formId ? `Form ID: ${formId}` : 'New Form'}
            {formData?.status && ` | Status: ${formData.status}`}
            <br/>
            SFP ID: {sfpId || 'None'}
          </div> */}
        </div>
        <div>
          {/* Publish button removed - now handled by toolbar */}
        </div>
      </div>
      
      {customAssessment !== null ? (
        <SurveyCreatorRenderComponent 
          customAssessment={customAssessment} 
          onSave={handleSaveForm}
          onPublish={handlePublishForm}
          formId={formId}
          sfpId={sfpId}
          formData={formData}
          showSuccess={showSuccess}
          showError={showError}
        />
      ) : (
        <div style={{ padding: '20px' }}>
          <p>Waiting for survey data to load...</p>
        </div>
      )}
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;